/* Copyright (c) 2020-present CloudIO Inc */

import { Platform } from '../../platform/Platform';
import { ActionManager, ActionManagerFn } from '../../registry/Component.type';
import { baseActionManager, baseActionProps } from '../../registry/Components';
import { analyzeActionDelete } from '../ActionDeleteCurrentRecord/ActionDeleteAnalyze';
import { defaultValue } from './ActionDeleteSelectedRecords';

const ActionDeleteSelectedRecordsManager: ActionManagerFn<
  'ActionDeleteSelectedRecords'
> = (platform: Platform): ActionManager<'ActionDeleteSelectedRecords'> => {
  return {
    ...baseActionManager(platform, 'ActionDeleteSelectedRecords'),
    defaultValue,
    props: {
      ...baseActionProps,
      alias: {
        label: 'Alias',
        type: 'DataSourceAliasChooser',
        required: true,
        allowedTypes: [
          'CloudDataStore',
          'DataStore',
          'LocalDataStore',
          'TransientDataStore',
        ],
      },
      confirmationTitle: {
        label: 'Confirmation Dialog Title',
        type: 'expression',
        returnType: async () => 'string',
      },
      confirmationMessage: {
        label: 'Confirmation Message',
        type: 'expression',
        returnType: async () => 'string',
        helpText: 'Leave blank to skip the confirmation dialog',
      },
    },
    name: 'Mark Selected Records for Deletion',
    getDisplayText: (item) => item.props.alias,
    isDataSourceAliasUsed: (item, alias) => item.props.alias === alias,
    icon: { iconName: 'trash' },
    analyze: analyzeActionDelete,
    description: `**Overview**<br/>
The "Mark Selected Records for Deletion" action is a batch data management component designed to flag multiple records for removal simultaneously. Unlike the single-record variant, this action targets the specific subset of records currently "selected" within the UI (e.g., rows checked in a data grid) and marks them all as deleted in the local Data Store state.<br/><br/>
Crucially, this is a deferred operation; it modifies the local state of the records but does not immediately remove them from the backend database. To finalize the deletion, an Action Save or Action Save Transaction must be executed subsequently. It includes built-in functionality to prompt the user for confirmation before applying the "delete" flag to the batch.

**Key Features**<br/>
* **Batch Processing:** Specifically designed to handle bulk operations, processing all records currently identified as "selected" in the target Data Store.
* **Deferred Commitment:** Adheres to the platform's standard data lifecycle by marking records for deletion locally, allowing for review or cancellation before permanent persistence.
* **Integrated User Confirmation:** Supports a configurable confirmation dialog via confirmationTitle and confirmationMessage, ensuring users don't accidentally delete multiple records at once.
* **Dynamic Feedback:** Properties support expressions, allowing the confirmation message to be dynamic (e.g., "Are you sure you want to delete these 5 items?").
* **Broad Store Compatibility:** Works with CloudDataStore, DataStore, LocalDataStore, and TransientDataStore.

**Properties**<br/>
* **Alias (Required):** A DataSourceAliasChooser property identifying the Data Store holding the selected records.
* **Confirmation Dialog Title:** An expression property defining the title of the verification pop-up.
* **Confirmation Message:** An expression property defining the body text of the pop-up.
  * *Behavior:* If left blank, the system bypasses the user confirmation step and marks the records immediately.

**Use Cases**<br/>
* **Bulk Cleanup:** A "Delete Selected" button above a data grid allows users to check multiple checkboxes and remove several rows in a single action.
* **Draft Management:** Clearing multiple temporary items from a TransientDataStore (e.g., removing selected items from a shopping cart or wishlist) before checking out.`,
  };
};

export default ActionDeleteSelectedRecordsManager;
